import { useFormik } from "formik"
import { enqueueSnackbar } from "notistack"
import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { useDispatch, useSelector } from "react-redux"
import { array, mixed, number, object, ObjectSchema, string } from "yup"

import { ProjectFormPageProps } from "components/ProjectForm"
import { isCpoDealType, resolveCpoProjectCategory } from "components/ProjectForm/cpoDefaults"
import { setProjectBasicDetailsForm, setProjectId } from "components/ProjectForm/ProjectFormSlice"
import { DEAL_TYPE } from "components/RaiseTicketForm/config/projectFormConstants"
import { EAccountRole } from "enums/commonEnums"
import EModuleIds from "enums/ModuleEnum"
import { AccountBillingAddressDetails, useLazyGetAccountDetailsQuery } from "pages/Accounts/AccountsServices"
import {
    useAddPurchaseOrderMutation,
    useGetAllProgramsListQuery,
    useGetDealDoneByUsersQuery,
    useGetProductsQuery,
    useGetPurchaseOrdersQuery,
    useLazyGetAccountsCompactedQuery,
} from "pages/Common/commonService"
import { DropdownObj } from "pages/Common/commonTypes"
import useProjectCategoryOptions from "hooks/useProjectCategoryOptions"
import { useAddProjectTicketMutation } from "pages/Projects/ProjectServices"
import { TStore } from "store/Store"

export enum EProjectBasicDetailsFields {
    PROJECT_NAME = "project_name",
    DEAL_DONE_BY = "deal_done_by_id",
    PROJECT_CLOSURE_DATE = "project_closure_date",
    PROGRAM = "program_id",
    CATEGORY = "category_id",
    PURCHASE_ORDER = "purchase_order",
}

export enum EPurchaseOrderFields {
    PO_NAME = "po_name",
    PO_NUMBER = "po_number",
    ACCOUNT = "account",
    BILLING_ADDRESS = "billing_address",
    PO_DATE = "po_date",
    PO_RECEIVE_DATE = "po_receive_date",
    PO_DEAL_DONE_BY = "po_deal_done_by",
    TOTAL_PO_VALUE = "total_po_value",
    SHIPPING_CHARGES = "shipping_charges",
    REMARKS = "remarks",
    PO_FILE = "po_file",
}

export enum EPurchaseOrderItemFields {
    SKU_CODE = "sku_code",
    QUANTITY = "quantity",
    RATE = "rate",
    DISCOUNT_VALUE = "discount_value",
    DISCOUNT_TYPE = "discount_type",
    TAX = "tax",
    TOTAL_AMOUNT = "total_amount",
}

export type PurchaseOrderItem = {
    [EPurchaseOrderItemFields.SKU_CODE]: DropdownObj | null
    [EPurchaseOrderItemFields.QUANTITY]: string
    [EPurchaseOrderItemFields.RATE]: string
    [EPurchaseOrderItemFields.DISCOUNT_VALUE]: string
    [EPurchaseOrderItemFields.DISCOUNT_TYPE]: DropdownObj | null
    [EPurchaseOrderItemFields.TAX]: string
    [EPurchaseOrderItemFields.TOTAL_AMOUNT]: string
}

export type ProjectBasicDetailsFormValues = {
    [EProjectBasicDetailsFields.PROJECT_NAME]: string
    [EProjectBasicDetailsFields.DEAL_DONE_BY]: DropdownObj | null
    [EProjectBasicDetailsFields.PROJECT_CLOSURE_DATE]: string
    [EProjectBasicDetailsFields.PROGRAM]: DropdownObj | null
    [EProjectBasicDetailsFields.CATEGORY]: DropdownObj | null
    [EProjectBasicDetailsFields.PURCHASE_ORDER]: DropdownObj | null
}

export type PurchaseOrderFormValues = {
    [EPurchaseOrderFields.PO_NAME]: string
    [EPurchaseOrderFields.PO_NUMBER]: string
    [EPurchaseOrderFields.ACCOUNT]: DropdownObj | null
    [EPurchaseOrderFields.BILLING_ADDRESS]: DropdownObj | null
    [EPurchaseOrderFields.PO_DATE]: string
    [EPurchaseOrderFields.PO_RECEIVE_DATE]: string
    [EPurchaseOrderFields.PO_DEAL_DONE_BY]: DropdownObj | null | undefined
    [EPurchaseOrderFields.TOTAL_PO_VALUE]: string | null | undefined
    [EPurchaseOrderFields.SHIPPING_CHARGES]: string | null | undefined
    [EPurchaseOrderFields.REMARKS]: string | null | undefined
    [EPurchaseOrderFields.PO_FILE]: File | null
    items: PurchaseOrderItem[]
    // Mirrors PaymentTerms.tsx's hardcoded "payment_terms_obj"/"total_amount" field paths so the
    // Payment Details step's PaymentTerms component can be reused here as-is. Optional since the
    // other reuse sites of PurchaseOrderFormValues (Logistics, the generic Purchase Order modal)
    // don't render payment terms and never set these.
    payment_terms_obj?: any[]
    total_amount?: string
}

const initialValues: ProjectBasicDetailsFormValues = {
    [EProjectBasicDetailsFields.PROJECT_NAME]: "",
    [EProjectBasicDetailsFields.DEAL_DONE_BY]: null,
    [EProjectBasicDetailsFields.PROJECT_CLOSURE_DATE]: "",
    [EProjectBasicDetailsFields.PROGRAM]: null,
    [EProjectBasicDetailsFields.CATEGORY]: null,
    [EProjectBasicDetailsFields.PURCHASE_ORDER]: null,
}

const createNewItem = (): PurchaseOrderItem => ({
    [EPurchaseOrderItemFields.SKU_CODE]: null,
    [EPurchaseOrderItemFields.QUANTITY]: "",
    [EPurchaseOrderItemFields.RATE]: "",
    [EPurchaseOrderItemFields.DISCOUNT_VALUE]: "",
    [EPurchaseOrderItemFields.DISCOUNT_TYPE]: { id: 1, title: "%" },
    [EPurchaseOrderItemFields.TAX]: "",
    [EPurchaseOrderItemFields.TOTAL_AMOUNT]: "",
})

const initialPurchaseOrderValues: PurchaseOrderFormValues = {
    [EPurchaseOrderFields.PO_NAME]: "",
    [EPurchaseOrderFields.PO_NUMBER]: "",
    [EPurchaseOrderFields.ACCOUNT]: null,
    [EPurchaseOrderFields.BILLING_ADDRESS]: null,
    [EPurchaseOrderFields.PO_DATE]: "",
    [EPurchaseOrderFields.PO_RECEIVE_DATE]: "",
    [EPurchaseOrderFields.PO_DEAL_DONE_BY]: null,
    [EPurchaseOrderFields.TOTAL_PO_VALUE]: null,
    [EPurchaseOrderFields.SHIPPING_CHARGES]: null,
    [EPurchaseOrderFields.REMARKS]: null,
    [EPurchaseOrderFields.PO_FILE]: null,
    items: [createNewItem()],
    payment_terms_obj: [],
    total_amount: "",
}

const dropdownSchema = object({
    id: number().required(),
    title: string().required(),
}).nullable()

const optionalStringSchema = string().trim().nullable().notRequired()
const requiredStringSchema = string().trim().required("This field is required")
const requiredDropdownSchema = dropdownSchema.required("This field is required")
const optionalDropdownSchema = dropdownSchema.defined()

const buildBasicDetailsValidationSchema = (requireSalesOrder: boolean): ObjectSchema<ProjectBasicDetailsFormValues> =>
    object({
        [EProjectBasicDetailsFields.PROJECT_NAME]: requiredStringSchema.label("Project Name"),
        [EProjectBasicDetailsFields.DEAL_DONE_BY]: requiredDropdownSchema.label("Deal Done by"),
        [EProjectBasicDetailsFields.PROJECT_CLOSURE_DATE]: requiredStringSchema.label(
            "Expected Project Closure Date"
        ),
        [EProjectBasicDetailsFields.PROGRAM]: dropdownSchema,
        [EProjectBasicDetailsFields.CATEGORY]: requiredDropdownSchema.label("Project Category"),
        [EProjectBasicDetailsFields.PURCHASE_ORDER]: requireSalesOrder
            ? requiredDropdownSchema.label("Sales Order")
            : optionalDropdownSchema,
    })

const purchaseOrderItemSchema = object({
    [EPurchaseOrderItemFields.SKU_CODE]: dropdownSchema.required("SKU Code is required"),
    [EPurchaseOrderItemFields.QUANTITY]: string().trim().required("Quantity is required"),
    [EPurchaseOrderItemFields.RATE]: string().trim().required("Rate is required"),
    [EPurchaseOrderItemFields.DISCOUNT_VALUE]: string().nullable().notRequired(),
    [EPurchaseOrderItemFields.DISCOUNT_TYPE]: dropdownSchema.nullable(),
    [EPurchaseOrderItemFields.TAX]: string().nullable().notRequired(),
    [EPurchaseOrderItemFields.TOTAL_AMOUNT]: string().nullable().notRequired(),
})

const purchaseOrderValidationSchema = object({
    [EPurchaseOrderFields.PO_NAME]: requiredStringSchema.label("Purchase Order Name"),
    [EPurchaseOrderFields.PO_NUMBER]: requiredStringSchema.label("Purchase Order Number"),
    [EPurchaseOrderFields.ACCOUNT]: requiredDropdownSchema.label("Account"),
    [EPurchaseOrderFields.BILLING_ADDRESS]: requiredDropdownSchema.label("Billing Address"),
    [EPurchaseOrderFields.PO_DATE]: requiredStringSchema.label("Purchase Order Date"),
    [EPurchaseOrderFields.PO_RECEIVE_DATE]: requiredStringSchema.label("Purchase Order Receive Date"),
    [EPurchaseOrderFields.PO_DEAL_DONE_BY]: requiredDropdownSchema.label("Deal Done by"),
    [EPurchaseOrderFields.TOTAL_PO_VALUE]: optionalStringSchema.label("Total Purchase Order Value"),
    [EPurchaseOrderFields.SHIPPING_CHARGES]: optionalStringSchema.label("Shipping Charges"),
    [EPurchaseOrderFields.REMARKS]: optionalStringSchema.label("Remarks"),
    [EPurchaseOrderFields.PO_FILE]: mixed<File>()
        .transform(value =>
            value && typeof value === "object" && !(value instanceof File) && Object.keys(value).length === 0
                ? null
                : value
        )
        .nullable()
        .required("Purchase Order file is required"),
    items: array().of(purchaseOrderItemSchema).min(1, "At least one item is required").defined(),
})

const useProjectBasicDetails = (props: ProjectFormPageProps) => {
    const dispatch = useDispatch()
    const {
        account_n_sites_form,
        account_n_sites_form_values,
        project_basic_details_form,
        project_id,
        deal_details_form,
    } = useSelector((state: TStore) => state.ProjectForm)
    const { user_id } = useSelector((store: any) => store.verifyLogin)
    const [showPurchaseOrderForm, setShowPurchaseOrderForm] = useState(false)
    const [billingAddressOptions, setBillingAddressOptions] = useState<DropdownObj[]>([])
    const [isFetchingBillingAddresses, setIsFetchingBillingAddresses] = useState(false)

    const { data: programsData, isLoading: programsLoading } = useGetAllProgramsListQuery()
    // Raise-form picker: system categories are written only by automations, so they are excluded.
    const { options: categoryOptions, isFetching: isCategoriesFetching } = useProjectCategoryOptions()
    const [getAccountsCompacted] = useLazyGetAccountsCompactedQuery()
    const { data: dealDoneByUsersList, isFetching: isDealDoneByUsersFetching } = useGetDealDoneByUsersQuery()
    const buyerAccountId = account_n_sites_form_values?.accountRoles?.[EAccountRole.BUYER]?.account?.id as
        | number
        | undefined
    const { data: purchaseOrders, isLoading: isLoadingPurchaseOrder } = useGetPurchaseOrdersQuery(
        {
            po_type: "receivable",
            account_id: buyerAccountId,
            status: "Open",
        },
        {
            skip: !buyerAccountId,
            refetchOnFocus: true,
            refetchOnMountOrArgChange: true,
        }
    )
    const { data: productsData } = useGetProductsQuery()
    const [getAccountDetails] = useLazyGetAccountDetailsQuery()
    const [addProjectTicket, { isLoading: isAddingProjectTicket }] = useAddProjectTicketMutation()
    const [addPurchaseOrder, { isLoading: isAddingPurchaseOrder }] = useAddPurchaseOrderMutation()

    const isCpoDeal = isCpoDealType(deal_details_form?.deal_type?.title)
    const isOutrightDeal = deal_details_form?.deal_type?.title === DEAL_TYPE.OUTRIGHT
    const isFranchisePartner = !!(
        account_n_sites_form?.is_franchise_partner || account_n_sites_form_values?.is_franchise_partner
    )
    // The backend requires purchase_order only for Outright projects and for CPO projects with a
    // franchise Buyer (FOCO); optional otherwise. When the user is creating a brand-new SO inline
    // ("+ Upload New"), the dropdown itself stays optional since that SO is linked on submit instead.
    const requireSalesOrder = (isOutrightDeal || (isCpoDeal && isFranchisePartner)) && !showPurchaseOrderForm

    const basicDetailsValidationSchema = useMemo(
        () => buildBasicDetailsValidationSchema(requireSalesOrder),
        [requireSalesOrder]
    )

    const formik = useFormik<ProjectBasicDetailsFormValues>({
        initialValues: project_basic_details_form ? { ...project_basic_details_form } : { ...initialValues },
        validationSchema: basicDetailsValidationSchema,
        validateOnChange: false,
        onSubmit: handleSubmit,
        enableReinitialize: true,
    })

    const purchaseOrderFormik = useFormik<PurchaseOrderFormValues>({
        initialValues: { ...initialPurchaseOrderValues },
        validationSchema: purchaseOrderValidationSchema,
        validateOnChange: false,
        onSubmit: () => {},
    })

    const purchaseOrderOptions =
        purchaseOrders?.map(p => ({
            id: p.id,
            title: `${p.po_name} ${p.po_number ? `(${p.po_number})` : ""}`,
        })) || []

    const selectedPurchaseOrder = purchaseOrders?.find(
        p => p.id === formik.values[EProjectBasicDetailsFields.PURCHASE_ORDER]?.id
    )

    const programsOptions =
        programsData?.map((program: any) => ({
            id: program?.program_id,
            title: `${program?.display_id} (${program?.program_name})`,
        })) || []

    const dealDoneByUsersOptions = useMemo(() => {
        return (
            dealDoneByUsersList?.map((dealDoneByUser: any) => ({
                id: dealDoneByUser.id,
                title: dealDoneByUser.name,
            })) || []
        )
    }, [dealDoneByUsersList])

    const skuOptions = useMemo(
        () =>
            productsData
                ? productsData?.map(product => ({
                      id: product.id,
                      title: `${product.sku_code} - ${product.display_name}`,
                  }))
                : [],
        [productsData]
    )

    const buyerAccountName = account_n_sites_form_values?.accountRoles?.[EAccountRole.BUYER]?.account?.title

    // SB-8432: a CPO project's category follows from the deal itself — Statiq CPO, or Franchise once
    // the buyer is a franchise partner. Only an empty category is filled, on add and edit alike, so a
    // saved category or one the user picked by hand is never overwritten.
    useEffect(() => {
        if (!isCpoDeal || !categoryOptions.length) return
        if (formik.values[EProjectBasicDetailsFields.CATEGORY]) return

        const defaultCategory = resolveCpoProjectCategory(isFranchisePartner, buyerAccountName)
        const option = categoryOptions.find(category => category.title.toLowerCase() === defaultCategory.toLowerCase())
        if (option) formik.setFieldValue(EProjectBasicDetailsFields.CATEGORY, option)
    }, [
        isCpoDeal,
        categoryOptions,
        isFranchisePartner,
        buyerAccountName,
        formik.values[EProjectBasicDetailsFields.CATEGORY],
    ])

    const prevBuyerAccountIdRef = useRef(buyerAccountId)
    useEffect(() => {
        if (prevBuyerAccountIdRef.current !== undefined && prevBuyerAccountIdRef.current !== buyerAccountId) {
            formik.setFieldValue(EProjectBasicDetailsFields.PURCHASE_ORDER, null)
        }
        prevBuyerAccountIdRef.current = buyerAccountId
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [buyerAccountId])

    const openPurchaseOrderForm = async () => {
        if (formik.values[EProjectBasicDetailsFields.PURCHASE_ORDER]) {
            formik.setFieldValue(EProjectBasicDetailsFields.PURCHASE_ORDER, null)
        }

        // Prefill Buyer account and billing address from Account & Location page
        const buyerData = account_n_sites_form_values?.accountRoles?.Buyer
        if (buyerData?.account) {
            const buyerAccount = {
                id: buyerData.account.id as number,
                title: buyerData.account.title,
            }
            purchaseOrderFormik.setFieldValue(EPurchaseOrderFields.ACCOUNT, buyerAccount)

            // Fetch billing addresses for the buyer account
            setIsFetchingBillingAddresses(true)
            try {
                const accountData = await getAccountDetails(buyerAccount.id).unwrap()

                if (accountData) {
                    const billingAddresses: DropdownObj[] =
                        accountData.billing_addresses?.map((addr: AccountBillingAddressDetails) => ({
                            id: addr.id,
                            title: `${addr.address}, ${addr.city}, ${addr.state} - ${addr.pincode}`,
                        })) || []

                    setBillingAddressOptions(billingAddresses)

                    // Prefill billing address if it exists in buyer data
                    if (buyerData.billing_address) {
                        const matchingAddress = billingAddresses.find(addr => addr.id === buyerData.billing_address?.id)
                        if (matchingAddress) {
                            purchaseOrderFormik.setFieldValue(EPurchaseOrderFields.BILLING_ADDRESS, matchingAddress)
                        }
                    }
                }
            } catch (error) {
                console.error("Error fetching account details:", error)
                setBillingAddressOptions([])
            } finally {
                setIsFetchingBillingAddresses(false)
            }
        }

        setShowPurchaseOrderForm(true)
    }

    const closePurchaseOrderForm = () => {
        setShowPurchaseOrderForm(false)
    }

    const hasError = (field: EProjectBasicDetailsFields) => Boolean(formik.submitCount > 0 && formik.errors[field])
    const hasPurchaseOrderError = (field: EPurchaseOrderFields) =>
        Boolean((formik.submitCount > 0 || purchaseOrderFormik.touched[field]) && purchaseOrderFormik.errors[field])

    const hasItemsTableError = useMemo(() => {
        if (formik.submitCount === 0) return false
        const items = purchaseOrderFormik.values.items
        // Check if all items have empty required fields (no valid item)
        return items.every(
            item =>
                !item[EPurchaseOrderItemFields.SKU_CODE] &&
                !item[EPurchaseOrderItemFields.QUANTITY] &&
                !item[EPurchaseOrderItemFields.RATE]
        )
    }, [formik.submitCount, purchaseOrderFormik.values.items])

    const getItemFieldError = useCallback(
        (index: number, field: EPurchaseOrderItemFields): boolean => {
            if (formik.submitCount === 0) return false
            const item = purchaseOrderFormik.values.items[index]
            if (!item) return false

            // Check if the field is empty when it should have a value
            switch (field) {
                case EPurchaseOrderItemFields.SKU_CODE:
                    return !item[EPurchaseOrderItemFields.SKU_CODE]
                case EPurchaseOrderItemFields.QUANTITY:
                    return !item[EPurchaseOrderItemFields.QUANTITY] || item[EPurchaseOrderItemFields.QUANTITY] === ""
                case EPurchaseOrderItemFields.RATE:
                    return !item[EPurchaseOrderItemFields.RATE] || item[EPurchaseOrderItemFields.RATE] === ""
                default:
                    return false
            }
        },
        [formik.submitCount, purchaseOrderFormik.values.items]
    )

    const getHelperText = (field: EProjectBasicDetailsFields) =>
        formik.submitCount > 0 ? (formik.errors[field] as string | undefined) : undefined

    const handleAccountChange = useCallback(
        async (selectedAccount: DropdownObj | null) => {
            purchaseOrderFormik.setFieldValue(EPurchaseOrderFields.ACCOUNT, selectedAccount)
            purchaseOrderFormik.setFieldValue(EPurchaseOrderFields.BILLING_ADDRESS, null)

            if (!selectedAccount) {
                setBillingAddressOptions([])
                return
            }

            setIsFetchingBillingAddresses(true)

            try {
                const accountData = await getAccountDetails(selectedAccount.id).unwrap()

                if (accountData) {
                    const billingAddresses: DropdownObj[] =
                        accountData.billing_addresses?.map((addr: AccountBillingAddressDetails) => ({
                            id: addr.id,
                            title: `${addr.address}, ${addr.city}, ${addr.state} - ${addr.pincode}`,
                        })) || []

                    setBillingAddressOptions(billingAddresses)
                }
            } catch (error) {
                console.error("Error fetching account details:", error)
                setBillingAddressOptions([])
            } finally {
                setIsFetchingBillingAddresses(false)
            }
        },
        [getAccountDetails, purchaseOrderFormik]
    )

    const handleAssignToMe = () => {
        const selectedUser = dealDoneByUsersOptions?.find(user => user.id === user_id)
        if (selectedUser) {
            formik.setFieldValue(EProjectBasicDetailsFields.DEAL_DONE_BY, selectedUser)
        }
    }

    const addNewItem = () => {
        const currentItems = purchaseOrderFormik.values.items || []
        purchaseOrderFormik.setFieldValue("items", [...currentItems, createNewItem()])
    }

    const removeItem = (index: number) => {
        const currentItems = purchaseOrderFormik.values.items || []
        if (currentItems.length <= 1) {
            return // Prevent deletion if there's only one row
        }
        const newItems = currentItems.filter((_, i) => i !== index)
        purchaseOrderFormik.setFieldValue("items", newItems)
    }

    const updateItem = (index: number, field: EPurchaseOrderItemFields, value: any) => {
        const currentItems = purchaseOrderFormik.values.items || []
        const newItems = [...currentItems]
        newItems[index] = { ...newItems[index], [field]: value }

        // Calculate total amount when relevant fields change
        if (
            field === EPurchaseOrderItemFields.QUANTITY ||
            field === EPurchaseOrderItemFields.RATE ||
            field === EPurchaseOrderItemFields.DISCOUNT_VALUE ||
            field === EPurchaseOrderItemFields.DISCOUNT_TYPE ||
            field === EPurchaseOrderItemFields.TAX
        ) {
            const quantity = parseFloat(newItems[index][EPurchaseOrderItemFields.QUANTITY]) || 0
            const rate = parseFloat(newItems[index][EPurchaseOrderItemFields.RATE]) || 0
            const discountValue = parseFloat(newItems[index][EPurchaseOrderItemFields.DISCOUNT_VALUE]) || 0
            const discountType = newItems[index][EPurchaseOrderItemFields.DISCOUNT_TYPE]
            const tax = parseFloat(newItems[index][EPurchaseOrderItemFields.TAX]) || 0

            let subtotal = quantity * rate
            let discountAmount = 0

            if (discountType?.id === 1) {
                discountAmount = subtotal * (discountValue / 100)
            } else {
                discountAmount = discountValue
            }

            const afterDiscount = subtotal - discountAmount
            const taxAmount = afterDiscount * (tax / 100)
            const total = afterDiscount + taxAmount

            newItems[index][EPurchaseOrderItemFields.TOTAL_AMOUNT] = total.toFixed(2)
        }

        purchaseOrderFormik.setFieldValue("items", newItems)
    }

    // Auto-calculate Total PO Value from items total + shipping charges
    const calculatedTotalPOValue = useMemo(() => {
        const items = purchaseOrderFormik.values.items
        const itemsTotal = items.reduce((sum, item) => {
            const totalAmount = parseFloat(item[EPurchaseOrderItemFields.TOTAL_AMOUNT]) || 0
            return sum + totalAmount
        }, 0)
        const shippingCharges =
            parseFloat(purchaseOrderFormik.values[EPurchaseOrderFields.SHIPPING_CHARGES] || "0") || 0
        return (itemsTotal + shippingCharges).toFixed(2)
    }, [purchaseOrderFormik.values.items, purchaseOrderFormik.values[EPurchaseOrderFields.SHIPPING_CHARGES]])

    // Update Total PO Value when calculated value changes
    useEffect(() => {
        purchaseOrderFormik.setFieldValue(EPurchaseOrderFields.TOTAL_PO_VALUE, calculatedTotalPOValue)
        // Mirrored for the reused PaymentTerms component, which reads "total_amount" to calculate
        // each term's amount from its percentage.
        purchaseOrderFormik.setFieldValue("total_amount", calculatedTotalPOValue)
    }, [calculatedTotalPOValue])

    // Optional payment terms sent with the inline "+ Upload New" SO. Only rows the user actually
    // filled in (picked a payment type) are sent; an empty array behaves exactly like omitting the field.
    const purchaseOrderPaymentTerms = useMemo(() => {
        const terms = purchaseOrderFormik.values.payment_terms_obj || []
        return terms
            .filter((term: any) => term?.pmt_type?.id !== undefined && term?.pmt_type?.id !== "")
            .map((term: any) => {
                const isAmount = term?.allocation_type?.id === "amount"
                const isConditional = term?.pmt_type?.id === 1
                const payload: Record<string, any> = {
                    split_type: isAmount ? "amount" : "percentage",
                    pmt_type: term?.pmt_type?.id ?? 0,
                    bank_guarantee: !!term?.bank_guarantee,
                }

                if (isAmount) {
                    payload.pmt_amount = Number(term?.pmt_amount) || 0
                } else {
                    payload.pmt_percent = Number(term?.pmt_percent) || 0
                }

                if (isConditional) {
                    payload.condition_id = term?.condition?.id
                    payload.time_qty = Number(term?.time_qty) || 0
                    payload.time_uom = term?.time_uom?.id ?? 1
                } else {
                    payload.pmt_due_date = term?.pmt_due_date || ""
                }

                return payload
            })
    }, [purchaseOrderFormik.values.payment_terms_obj])

    const handlePOFileChange = (files: FileList) => {
        if (files && files.length > 0) {
            purchaseOrderFormik.setFieldValue(EPurchaseOrderFields.PO_FILE, files[0])
        }
    }

    async function handleSubmit(values: ProjectBasicDetailsFormValues) {
        // Validate purchase order form if it's visible
        if (showPurchaseOrderForm) {
            const poErrors = await purchaseOrderFormik.validateForm()
            if (Object.keys(poErrors).length > 0) {
                return
            }
        }

        try {
            const basicDetails: Record<string, any> = {
                [EProjectBasicDetailsFields.PROJECT_NAME]: values[EProjectBasicDetailsFields.PROJECT_NAME],
                [EProjectBasicDetailsFields.DEAL_DONE_BY]: values[EProjectBasicDetailsFields.DEAL_DONE_BY]?.id,
                [EProjectBasicDetailsFields.PROJECT_CLOSURE_DATE]:
                    values[EProjectBasicDetailsFields.PROJECT_CLOSURE_DATE],
                [EProjectBasicDetailsFields.PROGRAM]: values[EProjectBasicDetailsFields.PROGRAM]?.id,
                [EProjectBasicDetailsFields.CATEGORY]: values[EProjectBasicDetailsFields.CATEGORY]?.id,
            }

            if (!showPurchaseOrderForm) {
                basicDetails[EProjectBasicDetailsFields.PURCHASE_ORDER] =
                    values[EProjectBasicDetailsFields.PURCHASE_ORDER]?.id || null
            }

            const accountsPayload = account_n_sites_form.account_roles.map(roleData => ({
                account_id: roleData.account_id,
                account_type_id: roleData.account_type_id,
                billing_address_id: roleData.billing_address_id,
                contact_id: roleData.contact_id,
                contact_type_id: roleData.contact_type_id,
                ...(roleData.role === EAccountRole.BUYER
                    ? { is_franchise: account_n_sites_form.is_franchise_partner ? 1 : 0 }
                    : {}),
            }))

            const sitePayload = {
                destination_site_id: account_n_sites_form.destination_site_id,
            }

            const payload: Record<string, any> = {
                ...basicDetails,
                ...sitePayload,
                accounts: accountsPayload,
                template_id: deal_details_form.template_id?.id,
                deal_type: deal_details_form.deal_type?.id,
            }

            // Include project_id in payload for edit case
            if (project_id) {
                payload.project_id = project_id
            }

            const res = await addProjectTicket(payload).unwrap()
            const finalProjectId = project_id || res.project_id

            if (finalProjectId) {
                if (!project_id && res.project_id) {
                    dispatch(setProjectId(res.project_id))
                }

                let finalFormValues = { ...values }

                if (showPurchaseOrderForm) {
                    const poValues = purchaseOrderFormik.values

                    const products = poValues.items.map(item => ({
                        product_id: item[EPurchaseOrderItemFields.SKU_CODE]?.id,
                        quantity: parseFloat(item[EPurchaseOrderItemFields.QUANTITY]) || 0,
                        rate: parseFloat(item[EPurchaseOrderItemFields.RATE]) || 0,
                        discount: parseFloat(item[EPurchaseOrderItemFields.DISCOUNT_VALUE]) || 0,
                        discount_unit: item[EPurchaseOrderItemFields.DISCOUNT_TYPE]?.id === 1 ? "percent" : "amount",
                        tax_percent: parseFloat(item[EPurchaseOrderItemFields.TAX]) || 0,
                        total_amount: parseFloat(item[EPurchaseOrderItemFields.TOTAL_AMOUNT]) || 0,
                    }))

                    const formData = new FormData()
                    formData.append("po_name", poValues[EPurchaseOrderFields.PO_NAME])
                    formData.append("po_number", poValues[EPurchaseOrderFields.PO_NUMBER])
                    formData.append("po_amount", poValues[EPurchaseOrderFields.TOTAL_PO_VALUE] || "0")
                    formData.append("shipping_charges", poValues[EPurchaseOrderFields.SHIPPING_CHARGES] || "0")
                    formData.append("account_id", String(poValues[EPurchaseOrderFields.ACCOUNT]?.id || ""))
                    formData.append("uploaded_by", String(poValues[EPurchaseOrderFields.PO_DEAL_DONE_BY]?.id || ""))
                    formData.append("po_created_at", poValues[EPurchaseOrderFields.PO_DATE])
                    formData.append("remarks", poValues[EPurchaseOrderFields.REMARKS] || "")
                    formData.append("project_id", String(finalProjectId))
                    formData.append("module_id", String(EModuleIds.PROJECTS))
                    formData.append("products", JSON.stringify(products))

                    if (poValues[EPurchaseOrderFields.PO_FILE] !== null) {
                        formData.append("file", poValues[EPurchaseOrderFields.PO_FILE] as File)
                    }

                    if (purchaseOrderPaymentTerms.length > 0) {
                        formData.append("payment_terms", JSON.stringify(purchaseOrderPaymentTerms))
                    }

                    const poResult = await addPurchaseOrder(formData).unwrap()

                    if (poResult?.po_id) {
                        finalFormValues = {
                            ...values,
                            [EProjectBasicDetailsFields.PURCHASE_ORDER]: {
                                id: poResult.po_id,
                                title: `${poValues[EPurchaseOrderFields.PO_NAME]}${poValues[EPurchaseOrderFields.PO_NUMBER] ? ` (${poValues[EPurchaseOrderFields.PO_NUMBER]})` : ""}`,
                            },
                        }
                    }

                    if (poResult?.terms_saved === false) {
                        enqueueSnackbar(
                            poResult?.terms_error ||
                                "Sales Order created, but its payment terms couldn't be saved. Add them from the Sales Order directly.",
                            { variant: "warning" }
                        )
                    }
                }

                // Save form values to Redux after PO creation so the new PO is included
                dispatch(setProjectBasicDetailsForm(finalFormValues))

                // Add or remove Payment page based on purchase order status for CPO deals
                const hasPurchaseOrder =
                    values[EProjectBasicDetailsFields.PURCHASE_ORDER]?.id || showPurchaseOrderForm
                if (props.isCPODeal) {
                    if (hasPurchaseOrder && props.addPaymentPage) {
                        props.addPaymentPage()
                    } else if (!hasPurchaseOrder && props.removePaymentPage) {
                        props.removePaymentPage()
                    }
                }

                props.handleNextStep()
            }
        } catch (error: any) {
            const message = error?.data?.message || error?.data?.error
            if (message && /sales order/i.test(message) && !showPurchaseOrderForm) {
                formik.setFieldError(EProjectBasicDetailsFields.PURCHASE_ORDER, message)
                return
            }
            console.error("Error saving basic details:", error)
            enqueueSnackbar(message || "Error saving project details", { variant: "error" })
        }
    }

    return {
        states: {
            dealDoneByUsersOptions,
            showPurchaseOrderForm,
            programsOptions,
            programsLoading,
            categoryOptions,
            isCategoriesFetching,
            isAddingProjectTicket,
            formik,
            purchaseOrderFormik,
            skuOptions,
            accountsTrigger: getAccountsCompacted,
            billingAddressOptions,
            isFetchingBillingAddresses,
            purchaseOrderOptions,
            isLoadingPurchaseOrder,
            isAddingPurchaseOrder,
            isDealDoneByUsersFetching,
            buyerAccountName,
            selectedPurchaseOrder,
            requireSalesOrder,
        },
        functions: {
            closePurchaseOrderForm,
            openPurchaseOrderForm,
            hasError,
            hasPurchaseOrderError,
            hasItemsTableError,
            getItemFieldError,
            getHelperText,
            addNewItem,
            removeItem,
            updateItem,
            handleAccountChange,
            handlePOFileChange,
            handleAssignToMe,
        },
    }
}

export default useProjectBasicDetails
